import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { ChartContainer, ChartTooltip } from '@klear/ui/components/chart'
import type { ChartConfig } from '@klear/ui/components/chart'
import { DEFAULT_CURRENCY_CODE } from '@klear/constants/currency'
import { moneyFormatterFor } from '@klear/money/currency'
import { KlearDate } from '@klear/date'
import { useKlearFinancialAccount } from '@klear/ui/hooks/use-klear-financial-account'
import { useDailyBalancesFromTransactions } from '@/app/klear-account/balance/use-daily-balances'
import { useKlearAccountTransactions } from '@/hooks/use-klear-account-transactions'
import { DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE } from '@klear/constants'
import { useMemo } from 'react'

interface BalanceChartProps {
  historyWindowDays?: number
}

export default function BalanceChart({
  historyWindowDays = DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE
}: BalanceChartProps) {
  const { klearFinancialAccount, selectedAccountId } = useKlearFinancialAccount()
  const { transactions, isLoading, error } = useKlearAccountTransactions(historyWindowDays)

  const chartData = useDailyBalancesFromTransactions(
    transactions,
    klearFinancialAccount?.account ? klearFinancialAccount.balanceCash : undefined,
    historyWindowDays
  )

  const xAxisInterval = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return 0
    }
    return Math.floor(chartData.length / 10)
  }, [chartData])

  if (isLoading) {
    return <div className="h-[200px] w-full animate-pulse bg-muted" />
  }

  if (error) {
    return (
      <div className="h-[200px] w-full flex items-center justify-center text-destructive">
        Failed to load Klear Account balance
      </div>
    )
  }

  const chartConfig: ChartConfig = {
    balance: {
      label: 'Balance',
      color: '#0066FF'
    }
  }

  const yAxisWidth = 45

  const formatMoney = moneyFormatterFor(klearFinancialAccount?.balanceCurrency ?? DEFAULT_CURRENCY_CODE)

  return (
    <ChartContainer config={chartConfig} className="h-[200px] w-full">
      <AreaChart
        key={selectedAccountId ?? 'none'}
        data={chartData}
        margin={{ left: 45, right: 15, top: 10, bottom: 10 }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={(value: string) => KlearDate.format(KlearDate.parse(value), 'MM/DD')}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 12 }}
          interval={xAxisInterval}
        />
        <YAxis
          width={yAxisWidth}
          tickFormatter={(value: number) => formatMoney(value, { decimals: 'none' })}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 12 }}
        />
        <ChartTooltip
          cursor={{ stroke: '#E5E7EB' }}
          wrapperStyle={{ outline: 'none' }}
          labelFormatter={(value) => KlearDate.format(KlearDate.parse(value as string), 'MMM D, YYYY')}
          formatter={(value) => formatMoney(value as number)}
        />
        <Area type="monotone" dataKey="balance" stroke="#0066FF" fill="rgba(0, 102, 255, 0.1)" strokeWidth={2} />
      </AreaChart>
    </ChartContainer>
  )
}
